import React from 'react';
import { ResumeData, Template, UserOverrides } from './resumeTypes';
import A4Sheet from './A4Sheet';
import {
  applyOverrides,
  resolveBgColor,
  getLuminance,
  getTextColorForBackground,
} from './resumeUtils';
import { Template01Renderer } from './Template01';
import Template02Renderer from './Template02';
import Template03Renderer from './Template03';

interface ResumeRendererProps {
  data: ResumeData;
  template: Template;
  overrides: UserOverrides;
  className?: string;
}

const ResumeRenderer: React.FC<ResumeRendererProps> = React.memo(({
  data,
  template,
  overrides,
  className = '',
}) => {
  const appliedTemplate = applyOverrides(template, overrides);

  const accentColor = appliedTemplate.colors?.accent
    || (template.id === 'template-01'
        ? (appliedTemplate.colors?.primary || '#303030')
        : (appliedTemplate.colors?.secondary || '#ECECEC'))
    || '#303030';

  const accentTextColor = getTextColorForBackground(accentColor);
  const isAccentDark = getLuminance(accentColor) < 128;
  const bgColor = resolveBgColor(appliedTemplate);
  const bgTextColor = getTextColorForBackground(bgColor);

  const renderTemplate = () => {
    switch (template.id) {
      case 'template-01':
        return (
          <Template01Renderer
            data={data}
            template={appliedTemplate}
            accentColor={accentColor}
            accentTextColor={accentTextColor}
            isAccentDark={isAccentDark}
            bgColor={bgColor}
            bgTextColor={bgTextColor}
          />
        );
      case 'template-02':
        return (
          <Template02Renderer
            data={data}
            template={appliedTemplate}
            accentColor={accentColor}
            accentTextColor={accentTextColor}
          />
        );
      case 'template-03':
        return (
          <Template03Renderer
            data={data}
            template={appliedTemplate}
            accentColor={accentColor}
            accentTextColor={accentTextColor}
          />
        );
      default:
        return (
          <Template02Renderer
            data={data}
            template={appliedTemplate}
            accentColor={accentColor}
            accentTextColor={accentTextColor}
          />
        );
    }
  };

  const fontFamily = (appliedTemplate.fonts?.baseFamily?.length)
    ? `${appliedTemplate.fonts.baseFamily.map((f: string) => `'${f}'`).join(', ')}, sans-serif`
    : undefined;

  return (
    <A4Sheet isPreview={false} className={`template-${template.id} ${className}`} fontFamily={fontFamily}>
      <div className="resume-content" style={{ padding: 0, height: '100%', boxSizing: 'border-box', fontFamily: fontFamily || undefined }}>
        {renderTemplate()}
      </div>
    </A4Sheet>
  );
});

ResumeRenderer.displayName = 'ResumeRenderer';
export default ResumeRenderer;
